/**
 * Rulebook — Official DUEL competition rules.
 * Published, versioned, community-accessible.
 */
import { useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import { RADIUS, FONT, SPACE } from '@/constants/designTokens';
import { ChevronDown, ChevronUp , ArrowLeft, Book, Shield, Scale, Trophy, Eye, AlertTriangle } from 'lucide-react-native';

const RULEBOOK_VERSION = '1.1.0';
const EFFECTIVE_DATE = 'March 29, 2026';

interface RuleSection {
  icon: any;
  title: string;
  rules: string[];
}

const SECTIONS: RuleSection[] = [
  {
    icon: Scale,
    title: 'LIFT STANDARDS',
    rules: [
      'Squat: Hip crease must break below the top of the knee (parallel or deeper).',
      'Bench Press: Bar must touch the chest, pause, then press to full lockout with hips on the bench.',
      'Deadlift: Bar must be lifted to full lockout — knees and hips extended, shoulders back. No hitching.',
      'Weighted Pull-Up: Full extension at the bottom, chin clearly over the bar at the top. Added weight only (bodyweight is automatic).',
      'Dead Hang: Hang from the bar at full arm extension for maximum time. Feet may not touch the floor. Time in seconds is your score.',
    ],
  },
  {
    icon: Trophy,
    title: 'COMPETITION FORMAT',
    rules: [
      'Seasons run 12 weeks (4 per year). Competitors are ranked by DUEL Standard Strength Score.',
      'Tournaments use Elo-seeded brackets (8–64 players). Single or double elimination.',
      'Duels are 1v1 matchups. Both lifters log their best lifts within the deadline. Higher total volume wins.',
      'Weekly Challenges run Monday–Sunday. One submission per user per challenge. Best score wins.',
      'Weight classes follow IPF standards. Lifters compete within their declared class.',
    ],
  },
  {
    icon: Eye,
    title: 'VIDEO VERIFICATION',
    rules: [
      'All ranked lifts (tournaments, season standings, challenge submissions) require video proof.',
      'Video must show the full rep from start to finish, including the lifter and the weight.',
      'Lifts are judged by a 3-judge panel. 2/3 white lights = good lift.',
      'Contested results may be escalated to a head judge for final ruling.',
      'Community judges must maintain >80% consensus accuracy to retain certification.',
    ],
  },
  {
    icon: Shield,
    title: 'INTEGRITY & FAIR PLAY',
    rules: [
      'Lifts are scored with a provenance multiplier: verified (video + judge approval) = 1.0×, attested (video submitted, pending review) = 0.85×, unverified (no video) = 0.70×.',
      'Lifts exceeding known world records are automatically rejected.',
      'Suspicious patterns (rapid gains, extreme bodyweight ratios) are flagged for review.',
      'Sandbagging (intentionally underperforming to manipulate brackets/matchmaking) is prohibited.',
      'Collusion between competitors to fix outcomes results in disqualification and rating reset.',
      'Multiple accounts are prohibited. Detection results in permanent ban.',
    ],
  },
  {
    icon: AlertTriangle,
    title: 'PENALTIES & APPEALS',
    rules: [
      'First offense (minor): Warning + lift voided.',
      'Second offense: 30-day competition ban + rating penalty (-100 Elo).',
      'Third offense: Permanent competition ban.',
      'Appeals may be filed within 7 days of a ruling by contacting support through the app.',
      'Appeal decisions by head judges are final.',
    ],
  },
];

export default function RulebookScreen() {
  const router = useRouter();
  const { theme } = useTheme();
  const c = theme.colors;
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set([0]));

  const toggleSection = (idx: number) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: c.background }} edges={['top']}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => router.back()} style={styles.backBtn}>
          <ArrowLeft size={22} color={c.text} />
        </TouchableOpacity>
        <Book size={20} color={c.accent} />
        <Text style={[styles.title, { color: c.text, fontFamily: TYPO.heading.bold }]}>RULEBOOK</Text>
      </View>

      <ScrollView contentContainerStyle={styles.content}>
        <View style={[styles.versionBanner, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Text style={[styles.versionText, { color: c.textSecondary }]}>
            Version {RULEBOOK_VERSION} · Effective {EFFECTIVE_DATE}
          </Text>
          <Text style={[styles.versionSub, { color: c.textTertiary }]}>
            These rules apply to all DUEL-sanctioned competitions.
          </Text>
        </View>

        {SECTIONS.map((section, idx) => {
          const Icon = section.icon;
          const isExpanded = expandedSections.has(idx);
          return (
            <View key={idx} style={[styles.section, { backgroundColor: c.surface, borderColor: c.border }]}>
              <TouchableOpacity
                style={styles.sectionHeader}
                onPress={() => toggleSection(idx)}
                activeOpacity={0.7}
                accessibilityRole="button"
                accessibilityLabel={`${section.title}, ${isExpanded ? 'collapse' : 'expand'}`}
              >
                <Icon size={16} color={c.accent} />
                <Text style={[styles.sectionTitle, { color: c.accent, fontFamily: TYPO.heading.bold, flex: 1 }]}>
                  {section.title}
                </Text>
                {isExpanded ? <ChevronUp size={16} color={c.textTertiary} /> : <ChevronDown size={16} color={c.textTertiary} />}
              </TouchableOpacity>
              {isExpanded && section.rules.map((rule, rIdx) => (
                <View key={rIdx} style={styles.ruleRow}>
                  <Text style={[styles.ruleNumber, { color: c.accent }]}>{rIdx + 1}.</Text>
                  <Text style={[styles.ruleText, { color: c.text }]}>{rule}</Text>
                </View>
              ))}
            </View>
          );
        })}

        <Text style={[styles.footer, { color: c.textTertiary }]}>
          Questions about the rules? Contact support@douevenlift.com
        </Text>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, gap: 10 },
  backBtn: { padding: 4 },
  title: { fontSize: FONT.xl, letterSpacing: 1 },
  content: { padding: 16, gap: SPACE.md, paddingBottom: 40 },
  versionBanner: { borderWidth: 1, borderRadius: RADIUS.xs, padding: 14, gap: 4 },
  versionText: { fontSize: 12, fontWeight: '600' },
  versionSub: { fontSize: 11 },
  section: { borderWidth: 1, borderRadius: RADIUS.xs, padding: 16, gap: SPACE.sm },
  sectionHeader: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 },
  sectionTitle: { fontSize: 12, letterSpacing: 1.5 },
  ruleRow: { flexDirection: 'row', gap: 8, paddingLeft: 4 },
  ruleNumber: { fontSize: 13, fontWeight: '700', width: 20 },
  ruleText: { fontSize: 13, lineHeight: 20, flex: 1 },
  footer: { fontSize: 11, textAlign: 'center', marginTop: SPACE.md },
});
